import {
  ALL_MODULES,
  getTranslatedText,
  ENTITY_MAP,
  COMMON_CONSTANTS
} from '@nile/components';
import {
  AccountClient,
  getApiMethodAndKeyNames,
  apiResolver,
  GET_FIELD,
  GET_REQUESTERS_BY_CRITERIA,
  GET_TECHNICIANS_BY_CRITERIA,
  TicketClient
} from '@nile/graphql';

/**
 * For entity type, if we want to call a specif api, this method will be called.
 * @param {object} item
 */
function getQuery(item) {
  const entity = item?.name?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return GET_TECHNICIANS_BY_CRITERIA;
    default:
      return GET_REQUESTERS_BY_CRITERIA;
  }
}

/**
 * After getting values from the API, this value specifies
 * the method name to process the data
 * @param {object} item
 */
function getNameKey(item) {
  const entity = item?.split('.')[0] || '';
  switch (entity) {
    case 'technician':
    case 'followers':
      return 'getTechnicianList';
    default:
      return 'getRequesterList';
  }
}

function formatOperatorValues(allOperators, operator, intl) {
  return allOperators.getRuleBuilderOperators.operator_placeholders[
    operator
  ]?.map((el) => {
    return {
      ...el,
      label: getTranslatedText(intl, el.key),
      value: getTranslatedText(intl, el.key),
      name: el.key
    };
  });
}

/**
 * This method gives the query and client using which the API calls will be made
 * after selecting the attribute
 * @param {object} item
 * @param {String} operator
 */

const getModulePathName = (item) => {
  let module = '',
    pathname = '';

  if (item?.name?.split('.')?.length > 1) {
    module = item.name.split('.').shift();
    pathname = item.name.split('.').pop();
  } else {
    module = 'project';
    pathname = item.name;
  }
  return { module, pathname };
};

function getPropValues(item, operator, allOperators, intl) {
  if (item?.name) {
    if (item.isCustom) {
      if (operator) {
        return formatOperatorValues(allOperators, operator, intl);
      }
      let module = '';
      let columnName = item?.name || item?.columnName;
      if (item.name.split('.')?.length > 1) {
        module = item.name.split('.').shift();
        columnName = item.name.split('.').pop();
      }
      return AccountClient.query({
        query: GET_FIELD,
        variables: {
          input: {
            columnName,
            module: ALL_MODULES?.[module]
          }
        }
      });
    }

    let { module, pathname } = getModulePathName(item);

    const query = apiResolver[ENTITY_MAP[module]][pathname]?.api;
    const client = apiResolver[ENTITY_MAP[module]][pathname]?.client;
    let variables = apiResolver[ENTITY_MAP[module]][pathname]?.variables || {};

    // if (item.name === 'client.name' || item.name === 'workItem.client.name') {
    //     variables = {
    //         listInfo: {
    //             sort: {
    //                 attribute: 'name',
    //                 order: 'ASC'
    //             }
    //         }
    //     };
    // }
    if (query && client) {
      return client.query({
        query: query,
        variables: variables
      });
    }
    if (operator) {
      return formatOperatorValues(allOperators, operator, intl);
    }
    return { client: AccountClient, query: getQuery(item) };
  }
  return {};
}

async function onAttributeSelect(params) {
  const { item: field, operator, fields, allOperators, intl } = params;
  const allFields = fields;
  const selectedField = allFields.find((el) => el.name === field);
  if (
    selectedField?.name !== 'client.name' &&
    selectedField?.name !== 'site.name' &&
    selectedField?.name !== 'owner.name'
  ) {
    if (field.split('.')?.length > 1) {
      const ent = field.split('.')[0];
      if (ent !== 'techGroup' && ent !== 'client')
        return getPropValues(selectedField, null, allOperators);
    }
  }
  if (selectedField?.dataType === 'boolean') {
    return [
      {
        name: 'true',
        label: 'true',
        value: true
      },
      {
        name: 'false',
        label: 'false',
        value: false
      }
    ];
  }
  const allValues = await getPropValues(
    selectedField,
    operator,
    allOperators,
    intl
  );
  let res = [];

  if (selectedField?.name) {
    let { module, pathname } = getModulePathName(selectedField);
    const apiMethodAndKeyNames = getApiMethodAndKeyNames(ENTITY_MAP[module]);
    let method = apiMethodAndKeyNames[pathname]?.method;
    const key = apiMethodAndKeyNames[pathname]?.key;
    const secondaryMethod = apiMethodAndKeyNames[pathname]?.secondaryMethod;
    const nestedKey = apiMethodAndKeyNames[pathname]?.nestedKey;

    let dataToMap = null;
    if (secondaryMethod) {
      dataToMap = allValues.data[method]?.[secondaryMethod];
    } else if (operator === 'on') {
      dataToMap = allValues;
    } else {
      dataToMap = allValues.data?.[method];
    }
    if (selectedField.nestedTo) {
      const nestedProp = apiMethodAndKeyNames[pathname]?.nestedKey;
      if (!dataToMap && !method) {
        dataToMap = allValues.data?.getField?.options;
      }
      dataToMap.forEach((el) => {
        if (el[nestedProp]) {
          res = res.concat(
            el[nestedProp].map((c) => {
              return {
                name: c[key],
                value: c[key],
                label: c[key]
              };
            })
          );
        } else {
          res = res.concat([
            { name: el?.value, value: el?.value, label: el?.value }
          ]);
        }
      });
    } else if (operator === 'on') {
      res = allValues;
    } else if (selectedField.isCustom) {
      method = 'getField';
      const fldOptions =
        allValues?.data?.[method]?.options ||
        allValues?.data?.[method]?.fieldOptions;
      res = allValues.data
        ? fldOptions?.map((el) => {
            return {
              name: el?.value,
              value: el?.value,
              label: el?.value
            };
          })
        : [];
    } else if (nestedKey) {
      const dataMap = dataToMap?.[nestedKey] || dataToMap;
      res = Array.isArray(dataMap)
        ? dataMap.map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    } else {
      res = dataToMap
        ? dataToMap.map((el) => {
            return {
              name: el[key],
              value: el[key],
              label: el[key]
            };
          })
        : [];
    }
  }
  return res;
}

/**
 * This method is called for autocomplete fields.
 * Usually apis will be called when we choose attributes. But for autocomplete,
 * on typing few characters, api will be called. This is the method.
 * @param {object} client
 * @param {gql} query
 * @param {object} params
 */
const onValueSearch = async (client, query, params) => {
  const attribute = params?.field?.split('.')[1];
  const nameKey = getNameKey(params.field);
  const variables = {
    listInfo: {
      condition: {
        joinOperator: 'AND',
        operands: [
          {
            attribute: attribute || 'firstName',
            operator: 'contains',
            value: params.inputValue
          }
        ]
      }
    }
  };
  const res = await client.query({
    query: query,
    variables: COMMON_CONSTANTS.IS_INSIDE_INPUT_VARIABLES[nameKey]
      ? { input: variables }
      : variables
  });
  const allOptions = res?.data?.[nameKey]
    ? res.data[nameKey].userList.map((el) => {
        return {
          name: `${el[attribute]}`,
          value: `${el[attribute]}`,
          label: `${el[attribute]}`
        };
      })
    : [];
  return allOptions;
};

export { onAttributeSelect, onValueSearch };
